/**
 * useResolveBilingual — resolves a `BilingualContent` (or plain string) to the
 * display string for the user's current teaching language + locale, reactively.
 *
 * Mirrors the local `useResolveBilingual` helper the role-play catalog screens
 * use, extracted to a shared hook so the Audio Lessons screens don't duplicate
 * it. Reads lessonLanguage / sourceLocale / targetLocale via individual
 * preference selectors (stable) and returns a memoized resolver.
 */

import { useCallback } from 'react';
import { useUserPreferencesStore } from '../store/userPreferencesStore';
import {
  selectLessonLanguage,
  selectSourceLocale,
  selectTargetLocale,
} from '../store/userPreferencesSelectors';
import { resolveBilingualContent, type BilingualContent } from '../types/lesson';

export function useResolveBilingual(): (content: BilingualContent | string) => string {
  const lessonLanguage = useUserPreferencesStore(selectLessonLanguage);
  const sourceLocale = useUserPreferencesStore(selectSourceLocale);
  const targetLocale = useUserPreferencesStore(selectTargetLocale);
  return useCallback(
    (content: BilingualContent | string) =>
      resolveBilingualContent(content, lessonLanguage, sourceLocale, targetLocale),
    [lessonLanguage, sourceLocale, targetLocale],
  );
}

/**
 * Resolve the TARGET-language form of a catalog term for display.
 *
 * A catalog row carries two things that look interchangeable and are not: the
 * headword (`lemma` / `text`, which is the catalog's KEY and always the American
 * form) and `translation`, whose `en_GB` holds the British rendering. Screens
 * were printing the headword raw, so an en-GB learner read `elevator`,
 * `fill out the form` and `Call 911` while the very same payload carried `lift`,
 * `fill in the form` and `Call 999`. 2,022 senses have an `en_GB` that differs
 * from their headword, so this was most of the variant data never reaching a
 * screen.
 *
 * Always `'target'`, never `lessonLanguage`: the headword is the thing being
 * LEARNED, so it stays in the target language regardless of which language the
 * learner reads explanations in. (`lessonLanguage` governs the gloss beneath it,
 * which is what `useResolveBilingual` is for.)
 *
 * Falls back to the headword whenever resolution yields nothing — a row with an
 * empty or missing translation must still render its term, never a blank line.
 * That fallback also makes this safe on rows the variant work never touched.
 *
 * Side effect worth knowing: this also fixes headwords that are themselves
 * British (`cashpoint`, whose `en_US` is correctly `ATM`). Keying display off
 * `translation` means an American sees `ATM` and a Briton sees `cashpoint`,
 * where before both saw the British lemma.
 */
export function useResolveTerm(): (
  term: string,
  translation?: BilingualContent | null,
) => string {
  const sourceLocale = useUserPreferencesStore(selectSourceLocale);
  const targetLocale = useUserPreferencesStore(selectTargetLocale);
  return useCallback(
    (term: string, translation?: BilingualContent | null) => {
      if (!translation) return term;
      return resolveBilingualContent(translation, 'target', sourceLocale, targetLocale) || term;
    },
    [sourceLocale, targetLocale],
  );
}

/** Humanize a kebab/underscore slug as a last-resort title when resolution misses. */
export function slugToTitle(slug: string): string {
  return slug
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}
